import { PeerSet } from '@libp2p/peer-collections';
import map from 'it-map';
import parallel from 'it-parallel';
import { pipe } from 'it-pipe';
import { ALPHA } from '../constants.js';
import { MessageType } from '../message/dht.js';
import { toPbPeerInfo } from '../message/utils.js';
import { queryErrorEvent, peerResponseEvent, providerEvent } from '../query/events.js';
export class ContentRouting {
    log;
    components;
    network;
    peerRouting;
    queryManager;
    routingTable;
    providers;
    constructor(components, init) {
        const { network, peerRouting, queryManager, routingTable, providers, logPrefix } = init;
        this.components = components;
        this.log = components.logger.forComponent(`${logPrefix}:content-routing`);
        this.network = network;
        this.peerRouting = peerRouting;
        this.queryManager = queryManager;
        this.routingTable = routingTable;
        this.providers = providers;
        this.findProviders = components.metrics?.traceFunction('libp2p.kadDHT.findProviders', this.findProviders.bind(this), {
            optionsIndex: 1,
            getAttributesFromYieldedValue: (event, attrs) => {
                if (event.name === 'PROVIDER') {
                    attrs.providers ??= [];
                    attrs.providers.push(...event.providers.map(info => info.id.toString()));
                }
                return attrs;
            }
        }) ?? this.findProviders;
        this.provide = components.metrics?.traceFunction('libp2p.kadDHT.provide', this.provide.bind(this), {
            optionsIndex: 1,
            getAttributesFromYieldedValue: (event, attrs) => {
                if (event.name === 'PEER_RESPONSE' && event.messageName === 'ADD_PROVIDER') {
                    attrs.providers ??= [];
                    attrs.providers.push(event.from.toString());
                }
                return attrs;
            }
        }) ?? this.provide;
    }
    /**
     * Announce to the network that we can provide the value for a given key and
     * are contactable on the given multiaddrs
     */
    async *provide(key, multiaddrs, options = {}) {
        this.log('provide %s', key);
        const target = key.multihash.bytes;
        // Add peer as provider
        await this.providers.addProvider(key, this.components.peerId);
        const msg = {
            type: MessageType.ADD_PROVIDER,
            key: target,
            providers: [
                toPbPeerInfo({
                    id: this.components.peerId,
                    multiaddrs
                })
            ]
        };
        let sent = 0;
        const maybeNotifyPeer = (event) => {
            return async () => {
                if (event.name !== 'FINAL_PEER') {
                    return [event];
                }
                const events = [];
                this.log('putProvider %s to %p', key, event.peer.id);
                try {
                    this.log('sending provider record for %s to %p', key, event.peer.id);
                    for await (const sendEvent of this.network.sendMessage(event.peer.id, msg, options)) {
                        if (sendEvent.name === 'PEER_RESPONSE') {
                            this.log('sent provider record for %s to %p', key, event.peer.id);
                            sent++;
                        }
                        events.push(sendEvent);
                    }
                }
                catch (err) {
                    this.log.error('error sending provide record to peer %p', event.peer.id, err);
                    events.push(queryErrorEvent({ from: event.peer.id, error: err }, options));
                }
                return events;
            };
        };
        // Notify closest peers
        yield* pipe(this.peerRouting.getClosestPeers(target, options), (source) => map(source, (event) => maybeNotifyPeer(event)), (source) => parallel(source, {
            ordered: false,
            concurrency: ALPHA
        }), async function* (source) {
            for await (const events of source) {
                yield* events;
            }
        });
        this.log('sent provider records to %d peers', sent);
    }
    /**
     * Search the dht for up to `K` providers of the given CID.
     */
    async *findProviders(key, options) {
        const toFind = this.routingTable.kBucketSize;
        let found = 0;
        const target = key.multihash.bytes;
        const self = this; // eslint-disable-line @typescript-eslint/no-this-alias
        this.log('findProviders %c', key);
        const provs = await this.providers.getProviders(key);
        // yield values if we have some, also slice because maybe we got lucky and already have too many?
        if (provs.length > 0) {
            const providers = [];
            for (const peerId of provs.slice(0, toFind)) {
                try {
                    const peer = await this.components.peerStore.get(peerId);
                    providers.push({
                        id: peerId,
                        multiaddrs: peer.addresses.map(({ multiaddr }) => multiaddr)
                    });
                }
                catch (err) {
                    if (err.name !== 'NotFoundError') {
                        throw err;
                    }
                    this.log('no peer store entry for %p', peerId);
                }
            }
            yield peerResponseEvent({ from: this.components.peerId, messageType: MessageType.GET_PROVIDERS, providers }, options);
            yield providerEvent({ from: this.components.peerId, providers }, options);
            found += providers.length;
            if (found >= toFind) {
                return;
            }
        }
        /**
         * The query function to use on this particular disjoint path
         */
        const findProvidersQuery = async function* ({ peer, signal }) {
            const request = {
                type: MessageType.GET_PROVIDERS,
                key: target
            };
            yield* self.network.sendRequest(peer, request, {
                ...options,
                signal
            });
        };
        const providers = new PeerSet(provs);
        for await (const event of this.queryManager.run(target, findProvidersQuery, options)) {
            yield event;
            if (event.name === 'PEER_RESPONSE') {
                this.log('Found %d provider entries for %c and %d closer peers', event.providers.length, key, event.closer.length);
                const newProviders = [];
                for (const peer of event.providers) {
                    if (providers.has(peer.id)) {
                        continue;
                    }
                    providers.add(peer.id);
                    newProviders.push(peer);
                }
                if (newProviders.length > 0) {
                    yield providerEvent({ from: event.from, providers: newProviders }, options);
                    found += newProviders.length;
                    if (found >= toFind) {
                        return;
                    }
                }
            }
        }
    }
}
//# sourceMappingURL=index.js.map